import React, { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Video } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import { AppButton, AppModal, Callout, EmptyState, PanelButton, SearchInput } from "../ui";

const formatDate = (dateStr) => (dateStr
  ? new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })
  : "");

const formatDuration = (seconds) => {
  if (!seconds) return "";
  const m = Math.round(seconds / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

const STATUS_NOTE = { processing: "Still processing", failed: "Failed to process" };

/**
 * MapRecordingModal — pick one of the batch's recordings (its Videos tab) for a
 * syllabus day or one of its topics. Reads GET /api/videos/batch/:batchId.
 *
 * Props:
 *   show, batchId, onClose
 *   targetLabel     — the day or topic it goes on
 *   alreadyMappedIds — recordings already there (listed, not choosable)
 *   onMap           — async (videoId); a throw is shown in the dialog
 */
const MapRecordingModal = ({ show, batchId, targetLabel, alreadyMappedIds = [], onMap, onClose }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mapping, setMapping] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!show || !batchId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setSelected(null);
    setSearch("");
    apiClient.get(`/videos/batch/${batchId}`, { params: { page: 1, limit: 200 } })
      .then(({ data }) => { if (!cancelled) setRecordings(data.data || []); })
      .catch(() => { if (!cancelled) { setError("The recordings could not be loaded."); setRecordings([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, batchId]);

  const handleMap = async () => {
    if (!selected) return;
    setMapping(true);
    setError("");
    try {
      await onMap(selected);
    } catch (err) {
      setError(err?.response?.data?.message || "The recording could not be mapped.");
    } finally {
      setMapping(false);
    }
  };

  const mappedSet = new Set(alreadyMappedIds.map(String));
  const q = search.trim().toLowerCase();
  const filtered = recordings.filter((r) => [r.title, r.topic].some((v) => (v || "").toLowerCase().includes(q)));

  return (
    <AppModal
      show={show}
      onHide={mapping ? undefined : onClose}
      size="lg"
      title="Map recording"
      icon={<Video size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onClose} disabled={mapping}>Cancel</AppButton>
          <AppButton onClick={handleMap} disabled={!selected || mapping}>
            {mapping ? "Mapping…" : "Map recording"}
          </AppButton>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        {targetLabel && (
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            To <span className="tw-font-semibold tw-text-content">{targetLabel}</span>
          </p>
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Search recordings…" />
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        {loading ? (
          <Shimmer height={58} count={3} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Video}
            title={recordings.length === 0 ? "No recordings yet" : "Nothing matches"}
            description={recordings.length === 0
              ? "This batch's class recordings appear on its Videos tab, and here, once they are processed."
              : "Try another search."}
          />
        ) : (
          <div className="tw-flex tw-max-h-[50vh] tw-flex-col tw-gap-2 tw-overflow-y-auto">
            {filtered.map((rec) => {
              const isMapped = mappedSet.has(String(rec._id));
              const meta = [formatDate(rec.recordedAt || rec.createdAt), formatDuration(rec.duration), STATUS_NOTE[rec.status]]
                .filter(Boolean).join(" · ");
              return (
                <PanelButton
                  key={rec._id}
                  selected={selected === rec._id}
                  disabled={isMapped}
                  onClick={() => setSelected(rec._id)}
                >
                  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-sunken tw-text-content-muted">
                    <Video size={18} aria-hidden="true" />
                  </span>
                  <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{rec.title || "Untitled recording"}</span>
                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-muted">
                      <CalendarDays size={12} aria-hidden="true" />
                      <span className="tw-truncate">{meta}</span>
                    </span>
                  </span>
                  {isMapped ? (
                    <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-content-subtle">Mapped</span>
                  ) : selected === rec._id && (
                    <CheckCircle2 size={18} aria-hidden="true" className="tw-shrink-0 tw-text-primary" />
                  )}
                </PanelButton>
              );
            })}
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default MapRecordingModal;
